import { Container, Box, Heading, Grid, GridItem, List, ListItem, Text } from '@chakra-ui/react';
import ImageShimmer from '../ImageShimmer/ImageShimmer';
import { TextWithArrowButton } from './Button';
import Testimonial from './Testimonial';
import ColoredSquares from './ColoredSquares';

import patternImage from '../../public/images/ui/sqaure-pattern-dark.svg';

export default function MasonryCard({
  card,
}: {
  card: {
    type: string;
    position?: boolean;
    bgDark?: boolean;
    image?: {
      sourceUrl: string;
      altText: string;
    };
    cta?: {
      title: string;
      author: {
        name: string;
        jobTitle: string;
      };
      button: {
        url: string;
        text: {
          strong: string;
          normal: string;
        };
        target?: string;
      };
    };
    testimonial?: {
      bgDark?: boolean;
      image: {
        sourceUrl: string;
        altText: string;
      };
      text: string;
      author: {
        name: string;
        jobTitle: string;
      };
    };
    title: string;
    text: {
      strong: string;
      normal: string;
    }[];
    button?: {
      type?: string;
      text: {
        normal?: string;
        strong?: string;
      };
      url: string;
      target?: boolean;
    }[];
    pills?: {
      item: string;
    }[];
  };
}) {
  return (
    <Container>
      <Grid
        gridTemplateColumns={{
          lg: '5fr 5fr',
          xl: card.position
            ? card.type === 'content_stats'
              ? '3.5fr 6.5fr'
              : '4.5fr 5.5fr'
            : card.type === 'content_stats'
            ? '6.5fr 3.5fr'
            : '5.5fr 4.5fr',
        }}
        gridGap={{ lg: '6' }}
        bgColor={card.type !== 'content_stats' ? 'white' : 'blue.100'}
      >
        <GridItem order={{ lg: card.position && 1 }}>
          {card.type !== 'content_cta' && card.type !== 'content_testimonial' && (
            <Box
              position="relative"
              overflow="hidden"
              w="100%"
              h={{
                base: card.type === 'content_stats' ? 'auto' : '18rem',
                sm: card.type === 'content_stats' ? 'auto' : '20rem',
                md: card.type === 'content_stats' ? 'auto' : '30rem',
                lg: '100%',
              }}
              borderStyle="solid"
              borderWidth={{
                base: card.type === 'content_stats' ? '0.5rem' : '0',
                lg: card.type === 'content_stats' ? '1.5rem' : '0',
              }}
              borderColor={card.type === 'content_stats' && 'blue.100'}
            >
              {card.type !== 'content_cta' && (
                <ImageShimmer
                  src={card.image.sourceUrl}
                  alt={card.image.altText}
                  style={{ objectFit: 'cover' }}
                  width={
                    (card.type === 'content_stats' && 688) || (card.type === 'content_story' && 568)
                  }
                  height={
                    (card.type === 'content_stats' && 538) || (card.type === 'content_story' && 383)
                  }
                  layout={
                    card.type === 'content_stats' || card.type === 'content_story'
                      ? 'responsive'
                      : 'fill'
                  }
                />
              )}
            </Box>
          )}
          {card.type === 'content_cta' && (
            <Box h={{ xl: '100%' }} p="14" bgColor="grey.900" position="relative">
              <Text
                fontSize={{ base: 'xl', lg: '1.75rem' }}
                lineHeight={{ base: '6', lg: '2.125rem' }}
                fontWeight="bold"
                color="white"
              >
                {card.cta.title}
              </Text>
              <Box
                mt={{ base: '6', lg: '8' }}
                color="white"
                fontSize={{ base: 'sm', lg: 'md' }}
                lineHeight={{ base: '1.25rem' }}
              >
                <Box fontWeight="bold">{card.cta.author.name}</Box>
                <Box>{card.cta.author.jobTitle}</Box>
              </Box>
              {card.cta.button && card.cta.button.url && (
                <TextWithArrowButton
                  type="dark"
                  href={card.cta.button.url}
                  text={{
                    strong: card.cta.button.text.strong,
                    normal: card.cta.button.text.normal,
                  }}
                  mt={{ base: '6', lg: '16' }}
                  target={card.cta.button.target ? '_blank' : '_self'}
                />
              )}
              <Box
                display={{ base: 'none', xl: 'block' }}
                w="16rem"
                h="14rem"
                position="absolute"
                right="4"
                bottom="4"
                bgImage={`url(${patternImage.src})`}
              />
            </Box>
          )}
          {card.type === 'content_testimonial' && (
            <Box
              h={{ lg: '100%' }}
              display={{ lg: 'flex' }}
              alignItems={{ lg: 'center' }}
              bgColor={card.testimonial.bgDark ? 'grey.900' : 'grey.100'}
            >
              <Testimonial
                testimonial={card.testimonial}
                bgColor={card.testimonial.bgDark ? 'grey.900' : 'grey.100'}
              />
            </Box>
          )}
        </GridItem>
        <GridItem
          bgColor={card.bgDark ? 'grey.900' : card.type !== 'content_story' && 'blue.100'}
          minH={{ xl: card.type !== 'content_story' && '35rem' }}
        >
          <Box h={{ lg: '100%' }} display={{ lg: 'flex' }} alignItems={{ lg: 'center' }}>
            <Box
              py={{ base: card.type !== 'content_story' && '12' }}
              pt={{
                base: '12',
                lg: card.type !== 'content_story' ? '12' : '0',
              }}
              pl={{
                base: card.type !== 'content_story' && '12',
                lg: card.type !== 'content_story' && '12',
                xl: card.type !== 'content_story' && '14',
              }}
              pr={{
                base: card.type !== 'content_story' && '12',
                lg: card.type !== 'content_story' && '10',
              }}
            >
              <ColoredSquares />
              <Heading
                as="h2"
                mt={{ base: '6', lg: '8' }}
                fontSize={{ base: '1.75rem', lg: '2rem' }}
                lineHeight={{ base: '2rem', lg: '2.25rem' }}
                color={card.bgDark ? 'white' : 'grey.900'}
              >
                {card.title}
              </Heading>
              <List mt={{ base: '6', lg: '8' }} spacing="4">
                {card.text.map((textItem, index) => (
                  <ListItem key={index}>
                    <Text color={card.bgDark ? 'white' : 'grey.900'}>
                      <Box as="strong">{textItem.strong}</Box>
                      <Box as="span"> {textItem.normal}</Box>
                    </Text>
                  </ListItem>
                ))}
              </List>
              {card.button && card.button.length && (
                <List spacing={{ base: '10', lg: '8' }} mt={{ base: '10', lg: '8' }}>
                  {card.button.map((button) => (
                    <ListItem key={button.url}>
                      <TextWithArrowButton
                        type={button.type}
                        href={button.url}
                        target={button.target ? '_blank' : '_self'}
                        text={{
                          strong: button.text.strong,
                          normal: button.text.normal,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {card.pills && card.pills.length && (
                <List mt="6" sx={{ '@media (min-width:768px)': { columns: '2' } }}>
                  {card.pills.map((pill, index) => (
                    <ListItem
                      display="inline-block"
                      mb="4"
                      mr="6"
                      py="2"
                      px="4"
                      key={index}
                      color="blue.800"
                      bgColor="white"
                      borderRadius="6.25rem"
                      fontWeight="500"
                      fontSize="sm"
                    >
                      {pill.item}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Container>
  );
}
